import { useCallback } from "react";

import type { DispatchViewStatePatch } from "@storyboard/dispatch-view/resolve";
import type { DispatchViewState } from "@storyboard/dispatch-view/view";
import { useWebSettings } from "@/models/StateProviders/WebSettingsProvider";

/**
 * The state laid over one Dispatch view, read from and written to `web.dispatch.viewState[viewId]` through the web
 * settings cascade.
 *
 * `state` is the compiled entry: the workspace's defaults with the member's own laid over them key by key. `write`
 * sends a sparse patch of the keys that changed, `null` for an override the member takes back; the server lays it over
 * the stored entry the same way, so the member's layer never carries what they did not touch. `reset` sends `null`
 * for the whole entry, which hands the view back to the workspace's defaults, and flushes it at once: a patch that
 * followed it into one debounced body would otherwise lay over the reset and cancel it, and the response carries the
 * defaults the reset uncovered.
 */
export function useDispatchViewState(viewId: string | undefined): {
  state: DispatchViewState | undefined;
  write: (patch: DispatchViewStatePatch) => void;
  reset: () => void;
} {
  const { settings, updateWebSettings, flushWebSettings } = useWebSettings();
  const stored: DispatchViewState | undefined = viewId ? settings.dispatch.viewState[viewId] : undefined;

  const write = useCallback(
    (patch: DispatchViewStatePatch) => {
      if (!viewId) return;
      updateWebSettings({ dispatch: { viewState: { [viewId]: patch } } });
    },
    [viewId, updateWebSettings],
  );

  const reset = useCallback(() => {
    if (!viewId) return;
    updateWebSettings({ dispatch: { viewState: { [viewId]: null } } });
    void flushWebSettings();
  }, [viewId, updateWebSettings, flushWebSettings]);

  return { state: stored, write, reset };
}
